// cspell:disable
/**
 * Extra nodes + edges for layout stress-testing (Dagre grid + viewport column wrap).
 *
 * Loaded automatically into `sampleNodes` / `sampleEdges` when mock graph API is on.
 * Set `REACT_APP_KG_GRAPH_API=mock` in `.env` to preview on the KG canvas.
 *
 * What this exercises:
 *   • 18 tables under Snowflake Analytics schema → hybrid grid overflow (base 3×4)
 *   • Mixed schema + schema-less data sources side by side
 *   • 40 stress ontologies, 45 metrics, 35 reports → column wrap past wrapBottomY
 *   • 25 data stories, 35 artifacts → downstream wrap columns
 */

import type { KgFlowNode } from "../types";

/** Counts tuned to overflow a ~900px canvas column and force wrap to column 2+. */
const STRESS_ONTOLOGY_COUNT = 40;
const STRESS_METRIC_COUNT = 45;
const STRESS_REPORT_COUNT = 35;
const STRESS_DATA_STORY_COUNT = 25;
const STRESS_ARTIFACT_COUNT = 35;

interface StressEdgeDef {
  source: string;
  target: string;
  label: string;
}

const tier = (label: string, pairs: [string, string][]): StressEdgeDef[] =>
  pairs.map(([source, target]) => ({ source, target, label }));

const dedupePairs = (pairs: [string, string][]): [string, string][] => {
  const seen = new Set<string>();
  return pairs.filter(([source, target]) => {
    const key = `${source}|${target}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const makeTable = (id: string, title: string): KgFlowNode => ({
  id,
  type: "table",
  position: { x: 0, y: 0 },
  data: {
    title,
    fields: [
      { name: "id", keyType: "PK" },
      { name: "name" },
      { name: "updated_at" },
    ],
  },
});

const makeOntology = (id: string, title: string): KgFlowNode => ({
  id,
  type: "ontology",
  position: { x: 0, y: 0 },
  data: { title },
});

const makeMetric = (id: string, title: string): KgFlowNode => ({
  id,
  type: "metric",
  position: { x: 0, y: 0 },
  data: { title },
});

const makeReport = (id: string, title: string): KgFlowNode => ({
  id,
  type: "report",
  position: { x: 0, y: 0 },
  data: {
    title,
    biName: "MagicBI",
    biLogoUrl: "/logos/magicbi.svg",
    reportSourceType: "magicbi",
  },
});

const makeDataStory = (id: string, title: string, status = "Published"): KgFlowNode => ({
  id,
  type: "dataStory",
  position: { x: 0, y: 0 },
  data: { title, status },
});

const makeArtifact = (
  id: string,
  title: string,
  kind: "PPT" | "PDF" | "Memo" | "Video" | "Doc",
): KgFlowNode => ({
  id,
  type: "artifact",
  position: { x: 0, y: 0 },
  data: { title, kind },
});

// ── Extra schema (BigQuery gets a schema branch) ──────────────────────────────

export const stressSchemas: KgFlowNode[] = [
  {
    id: "sch-bq-default",
    type: "schema",
    position: { x: 0, y: 0 },
    data: { title: "Default", subtitle: "Schema" },
  },
];

// ── Extra tables (grid overflow per schema) ─────────────────────────────────

/** 18 tables under Analytics — forces row/col expansion past the 3×4 base grid. */
const analyticsStressTables = Array.from({ length: 18 }, (_, index) =>
  makeTable(`tbl-sf-analytics-extra-${index + 1}`, `SF Analytics ${index + 1}`),
);

/** 8 tables under Staging — second sub-block inside the Snowflake DS block. */
const stagingStressTables = Array.from({ length: 8 }, (_, index) =>
  makeTable(`tbl-sf-staging-extra-${index + 1}`, `SF Staging ${index + 1}`),
);

/** 6 tables under BigQuery schema. */
const bqStressTables = Array.from({ length: 6 }, (_, index) =>
  makeTable(`tbl-bq-extra-${index + 1}`, `BQ Event ${index + 1}`),
);

/** 5 direct tables for Tableau (schema-less branch). */
const tableauStressTables = Array.from({ length: 5 }, (_, index) =>
  makeTable(`tbl-tableau-extra-${index + 1}`, `Tableau View ${index + 1}`),
);

export const stressTables: KgFlowNode[] = [
  ...analyticsStressTables,
  ...stagingStressTables,
  ...bqStressTables,
  ...tableauStressTables,
];

// ── Downstream layers (viewport column-wrap) ────────────────────────────────

export const stressOntologies: KgFlowNode[] = Array.from(
  { length: STRESS_ONTOLOGY_COUNT },
  (_, index) => makeOntology(`ont-stress-${index + 1}`, `Stress Domain ${index + 1}`),
);

export const stressMetrics: KgFlowNode[] = Array.from(
  { length: STRESS_METRIC_COUNT },
  (_, index) => makeMetric(`m-stress-${index + 1}`, `Stress KPI ${index + 1}`),
);

export const stressReports: KgFlowNode[] = Array.from(
  { length: STRESS_REPORT_COUNT },
  (_, index) => makeReport(`rpt-stress-${index + 1}`, `Stress Report ${index + 1}`),
);

export const stressDataStories: KgFlowNode[] = Array.from(
  { length: STRESS_DATA_STORY_COUNT },
  (_, index) =>
    makeDataStory(
      `dstory-stress-${index + 1}`,
      `Stress Story ${index + 1}`,
      index % 3 === 0 ? "Draft" : "Published",
    ),
);

const artifactKinds = ["PPT", "PDF", "Memo", "Video", "Doc"] as const;

export const stressArtifacts: KgFlowNode[] = Array.from(
  { length: STRESS_ARTIFACT_COUNT },
  (_, index) =>
    makeArtifact(
      `art-stress-${index + 1}`,
      `Stress Artifact ${index + 1}`,
      artifactKinds[index % artifactKinds.length],
    ),
);

export const stressNodes: KgFlowNode[] = [
  ...stressSchemas,
  ...stressTables,
  ...stressOntologies,
  ...stressMetrics,
  ...stressReports,
  ...stressDataStories,
  ...stressArtifacts,
];

// ── Edges ───────────────────────────────────────────────────────────────────

const analyticsTableIds = analyticsStressTables.map((node) => node.id);
const stagingTableIds = stagingStressTables.map((node) => node.id);
const bqTableIds = bqStressTables.map((node) => node.id);
const tableauTableIds = tableauStressTables.map((node) => node.id);
const allStressTableIds = [
  ...analyticsTableIds,
  ...stagingTableIds,
  ...bqTableIds,
  ...tableauTableIds,
];

const ontologyIds = stressOntologies.map((node) => node.id);
const metricIds = stressMetrics.map((node) => node.id);
const reportIds = stressReports.map((node) => node.id);
const storyIds = stressDataStories.map((node) => node.id);
const artifactIds = stressArtifacts.map((node) => node.id);

/**
 * Base sample node IDs — keep in sync with `sampleData.ts` so stress layers
 * receive edges from the main pipeline (not just stress-only tables).
 */
const BASE_TABLE_IDS = [
  "tbl-orders",
  "tbl-products",
  "tbl-customers",
  "tbl-inventory",
  "tbl-suppliers",
  "tbl-transactions",
  "tbl-employees",
  "tbl-departments",
  "tbl-campaigns",
  "tbl-returns",
  "tbl-shipments",
  "tbl-warehouses",
  "tbl-invoices",
  "tbl-payments",
  "tbl-subscriptions",
  "tbl-events",
  "tbl-sessions",
  "tbl-page-views",
  "tbl-sales-report",
  "tbl-product-catalog",
  "tbl-order-items",
  "tbl-channels",
] as const;

const BASE_ONTOLOGY_IDS = [
  "ont-batch",
  "ont-customer",
  "ont-region",
  "ont-product",
  "ont-order",
  "ont-campaign",
  "ont-employee",
  "ont-channel",
] as const;

const BASE_METRIC_IDS = [
  "m-gm",
  "m-aov",
  "m-oc",
  "m-conversion",
  "m-cac",
  "m-ltv",
  "m-churn",
  "m-mrr",
  "m-nps",
  "m-rev-growth",
  "m-inv-turn",
  "m-fulfillment",
  "m-cust-acq",
  "m-return-rate",
  "m-profit-margin",
  "m-sales-velocity",
  "m-emp-productivity",
  "m-campaign-roi",
  "m-support-tickets",
  "m-time-to-resolve",
] as const;

const BASE_REPORT_IDS = [
  "rpt-gross",
  "rpt-returns",
  "rpt-trend",
  "rpt-q3-orders",
  "rpt-cust-acq",
  "rpt-inventory",
  "rpt-employees",
  "rpt-campaigns",
  "rpt-churn",
  "rpt-finance",
  "rpt-marketing",
  "rpt-ops",
] as const;

const BASE_STORY_IDS = [
  "dstory-q3",
  "dstory-annual",
  "dstory-customer",
  "dstory-mktg",
  "dstory-supply",
  "dstory-exec",
] as const;

/** Round-robin: each stress table classifies into 2–3 ontologies. */
const classifyPairs: [string, string][] = allStressTableIds.flatMap((tableId, index) => {
  const primary = ontologyIds[index % ontologyIds.length];
  const secondary = ontologyIds[(index + 3) % ontologyIds.length];
  const tertiary = ontologyIds[(index + 7) % ontologyIds.length];
  const pairs: [string, string][] = [[tableId, primary]];
  if (secondary !== primary) pairs.push([tableId, secondary]);
  if (tertiary !== primary && tertiary !== secondary) pairs.push([tableId, tertiary]);
  return pairs;
});

/** Base tables also feed stress ontologies — lines span wrapped columns. */
const baseTableClassifyPairs: [string, string][] = BASE_TABLE_IDS.flatMap(
  (tableId, index) => [
    [tableId, ontologyIds[index % ontologyIds.length]] as [string, string],
    [tableId, ontologyIds[(index + 11) % ontologyIds.length]] as [string, string],
    [tableId, ontologyIds[(index + 23) % ontologyIds.length]] as [string, string],
  ],
);

/** Base ontologies measure stress metrics — bridges base → stress pipeline. */
const baseOntologyMeasurePairs: [string, string][] = BASE_ONTOLOGY_IDS.flatMap(
  (ontologyId, index) => [
    [ontologyId, metricIds[(index + 2) % metricIds.length]] as [string, string],
    [ontologyId, metricIds[(index + 12) % metricIds.length]] as [string, string],
  ],
);

/** Base metrics power stress reports. */
const baseMetricPowerPairs: [string, string][] = BASE_METRIC_IDS.map(
  (metricId, index) =>
    [metricId, reportIds[(index + 3) % reportIds.length]] as [string, string],
);

/** Base reports publish in stress stories. */
const baseReportPublishPairs: [string, string][] = BASE_REPORT_IDS.flatMap(
  (reportId, index) => [
    [reportId, storyIds[index % storyIds.length]] as [string, string],
    [reportId, storyIds[(index + 5) % storyIds.length]] as [string, string],
  ],
);

/** Base stories export to stress artifacts. */
const baseStoryExportPairs: [string, string][] = BASE_STORY_IDS.flatMap(
  (storyId, index) => [
    [storyId, artifactIds[index % artifactIds.length]] as [string, string],
    [storyId, artifactIds[(index + 7) % artifactIds.length]] as [string, string],
  ],
);

/** Each ontology measures 2 metrics (wraps metric column). */
const measurePairs: [string, string][] = ontologyIds.flatMap((ontologyId, index) => [
  [ontologyId, metricIds[index % metricIds.length]] as [string, string],
  [ontologyId, metricIds[(index + 5) % metricIds.length]] as [string, string],
]);

/** Each metric powers 1 report. */
const powerPairs: [string, string][] = metricIds.map(
  (metricId, index) =>
    [metricId, reportIds[index % reportIds.length]] as [string, string],
);

/** Each report published in 1–2 stories. */
const publishPairs: [string, string][] = reportIds.flatMap((reportId, index) => {
  const primary = storyIds[index % storyIds.length];
  const secondary = storyIds[(index + 2) % storyIds.length];
  return index % 2 === 0
    ? ([[reportId, primary], [reportId, secondary]] as [string, string][])
    : ([[reportId, primary]] as [string, string][]);
});

/** Each story exports to 1–2 artifacts. */
const exportPairs: [string, string][] = dedupePairs([
  ...storyIds.flatMap((storyId, index) => [
    [storyId, artifactIds[index % artifactIds.length]] as [string, string],
    [storyId, artifactIds[(index + 4) % artifactIds.length]] as [string, string],
  ]),
  /** Round-robin guarantee — tail artifacts in wrapped column 2 were orphaned. */
  ...artifactIds.map(
    (artifactId, index) =>
      [storyIds[index % storyIds.length], artifactId] as [string, string],
  ),
  ...artifactIds.map(
    (artifactId, index) =>
      [BASE_STORY_IDS[index % BASE_STORY_IDS.length], artifactId] as [string, string],
  ),
]);

export const stressEdgeDefs: StressEdgeDef[] = [
  ...tier("contains", [["ds-bigquery", "sch-bq-default"]]),
  ...tier(
    "ingests",
    [
      ...analyticsTableIds.map(
        (tableId) => ["sch-sf-analytics", tableId] as [string, string],
      ),
      ...stagingTableIds.map(
        (tableId) => ["sch-sf-staging", tableId] as [string, string],
      ),
      ...bqTableIds.map(
        (tableId) => ["sch-bq-default", tableId] as [string, string],
      ),
      ...tableauTableIds.map(
        (tableId) => ["ds-tableau", tableId] as [string, string],
      ),
    ],
  ),
  ...tier("classifies", [...classifyPairs, ...baseTableClassifyPairs]),
  ...tier("measures", [...measurePairs, ...baseOntologyMeasurePairs]),
  ...tier("powers", [...powerPairs, ...baseMetricPowerPairs]),
  ...tier("published in", [...publishPairs, ...baseReportPublishPairs]),
  ...tier("exports to", dedupePairs([...exportPairs, ...baseStoryExportPairs])),
];
